import React, { useEffect, useState } from 'react';
import { SkyrimFrame } from '../SkyrimFrame/SkyrimFrame';
import './CitySelection.scss';

// Fixed panel size (px), like AuthPanel — the frame is drawn at these exact
// dimensions and the whole panel is centred with a transform, so it can never
// be stretched by a parent flex container.
const PANEL_WIDTH = 980;
const PANEL_HEIGHT = 560;

interface CityData {
  id: string;
  name: string;
  hold: string;
  description: string;
}

// Each city's heraldic accent colour, used to tint its emblem mask.
const CITY_COLORS: Record<string, string> = {
  whiterun:   '#a7a058',
  solitude:   '#552a24',
  windhelm:   '#476f8e',
  riften:     '#4e3d4e',
  markarth:   '#2e351f',
  morthal:    '#414c3c',
  dawnstar:   '#332f3e',
  winterhold: '#e6ecec',
  falkreath:  '#363e52',
  // Sampled from emblem_bruma.png's own opaque-pixel average (warm gold),
  // not guessed - no canonical Bruma heraldry reference was available.
  bruma:      '#dcba5a',
};

// Hold emblems (real linework artwork, black on transparent). Rather than
// recolour the pixels, each PNG is used as a CSS mask over a solid fill in
// the city's heraldic colour — the emblem "shows through" the shape, on the
// same dark heater shield as the placeholder cities.
const EMBLEM_IMAGES: Record<string, string> = {
  whiterun: require('./img/emblem_whiterun.png').default,
  solitude: require('./img/emblem_solitude.png').default,
  windhelm: require('./img/emblem_windhelm.png').default,
  riften: require('./img/emblem_riften.png').default,
  markarth: require('./img/emblem_markarth.png').default,
  morthal: require('./img/emblem_morthal.png').default,
  dawnstar: require('./img/emblem_dawnstar.png').default,
  winterhold: require('./img/emblem_winterhold.png').default,
  falkreath: require('./img/emblem_falkreath.png').default,
  bruma: require('./img/emblem_bruma.png').default,
};

const EmblemBlason: React.FC<{ cityId: string }> = ({ cityId }) => {
  const color = CITY_COLORS[cityId] ?? '#9d9e9e';
  const imageUrl = EMBLEM_IMAGES[cityId];
  return (
    <div className="blason-emblem">
      <svg className="shield-svg" viewBox="0 0 100 120" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 5 H95 V65 Q95 95 50 115 Q5 95 5 65 Z" fill="#141109" stroke={color} strokeWidth="1.5" strokeOpacity="0.7" />
        <path d="M12 12 H88 V64 Q88 89 50 107 Q12 89 12 64 Z" fill="none" stroke={color} strokeWidth="0.8" strokeOpacity="0.35" />
      </svg>
      {/* Emblem PNGs are pre-tinted (baked-in colour, not recoloured at
          runtime): CEF here renders with GPU compositing disabled
          (see cef_debug.log "Passthrough is not supported, GL is disabled"),
          and CSS mask-image reliably renders blank without GPU compositing. */}
      <img className="blason-emblem__mark" src={imageUrl} alt="" />
    </div>
  );
};

// Ignore any click during the first moments after the screen opens: browser
// focus flaps while the game finishes loading, and a stray game click (or a
// held key) can land on a blason and silently pick a city.
const CLICK_GRACE_MS = 1200;

const CitySelection: React.FC = () => {
  const [cities, setCities] = useState<CityData[]>([]);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const openedAtRef = React.useRef(0);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { cities: CityData[] };
      setCities(detail.cities ?? []);
      setVisible(true);
      openedAtRef.current = Date.now();
    };
    window.addEventListener('skymp5-client:showCitySelection', handler);
    return () => window.removeEventListener('skymp5-client:showCitySelection', handler);
  }, []);

  const selectCity = (id: string, e: React.MouseEvent) => {
    // e.detail === 0 means the "click" came from the keyboard (Enter/Space on
    // the DOM-focused button) — only accept real pointer clicks.
    if (e.detail === 0) return;
    if (Date.now() - openedAtRef.current < CLICK_GRACE_MS) return;

    setVisible(false);
    if ((window as any).skyrimPlatform?.sendMessage) {
      (window as any).skyrimPlatform.sendMessage('city-select:' + id);
    }
  };

  if (!visible || cities.length === 0) return null;

  return (
    <div className="city-selection-overlay">
      <div
        className="city-selection-panel"
        style={{ width: `${PANEL_WIDTH}px`, height: `${PANEL_HEIGHT}px` }}
      >
        <div className="city-selection-panel__frame">
          <SkyrimFrame width={PANEL_WIDTH} height={PANEL_HEIGHT} header={false} name="citySelection" />
        </div>

        <div className="city-selection-panel__content">
          <h1 className="city-selection-title">Choisissez votre destination</h1>

          <div className="city-grid">
            {cities.map((city) => (
              <button
                key={city.id}
                className={`city-item${hovered === city.id ? ' city-item--hovered' : ''}`}
                onClick={(e) => selectCity(city.id, e)}
                onMouseEnter={() => setHovered(city.id)}
                onMouseLeave={() => setHovered(null)}
                type="button"
              >
                <div className="city-shield">
                  <EmblemBlason cityId={city.id} />
                </div>
                {/* <div className="city-item-name">{city.name}</div> */}
                {/* <div className="city-item-hold">{city.hold}</div> */}
              </button>
            ))}
          </div>

          {/* Tooltip description for hovered city */}
          <div className={`city-tooltip${hovered ? ' city-tooltip--visible' : ''}`}>
            {cities.find(c => c.id === hovered)?.description ?? ''}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CitySelection;
